import { buildFormValidate } from "@turo/libraries/lib/helpers/validationHelpers";
import { GhostButton, PrimaryButton, TextInput } from "@turo/pedal";
import { StyledText } from "@turo/ui";
import type { CountryCode } from "libphonenumber-js/mobile";
import { type FormEventHandler, useEffect, useState } from "react";

import { OTPButtonContainer } from "~/src/components/OTPButtonContainer";
import { OTPForm } from "~/src/components/OTPForm";
import { OtpResendPasscodeButton } from "~/src/components/OtpResendPasscodeButton";
import { OTP_METHODS } from "~/src/constants/otp";
import { onShowOtpPhoneConfirmStep } from "~/src/helpers/formEventActions";
import { dispatchLoginFailure } from "~/src/helpers/loginActions";
import { formatPhoneNumber } from "~/src/helpers/phoneNumberHelpers";
import { getRecaptchaToken } from "~/src/helpers/recaptcha";
import { OTP_TRACKING_EVENT, trackOtp } from "~/src/helpers/tracking";
import { useSimpleTranslate } from "~/src/SimpleTranslateProvider";
import type { StateFormField } from "~/types/stateForm";

import stateForm, { FIELD_TYPES } from "../../components/stateForm";
import { generateOTP, loginWithOTP } from "../../helpers/otpHelpers";
import {
  isPasscodeEmpty,
  otpPasscodeValidate,
} from "../../helpers/passcodeFormHelpers";
import type { OtpLoginPhoneTranslations } from ".";

/**
 * TODO-TS: `stateForm` props should be derived from `stateForm`
 */
interface PhoneConfirmOTPFormProps {
  buildSubmitHandler: (
    onSubmit: (formData: { passcode: string }) => Promise<unknown>,
  ) => FormEventHandler<HTMLFormElement>;
  countryCode: CountryCode;
  formFields: {
    passcode: StateFormField;
  };
  isLoading: boolean;
  loginStart: () => void;
  loginSuccess: (data: unknown) => void;
  onClickBack: () => void;
  onResendOtpTokenSuccess: (token: string) => void;
  otpToken: string;
  phoneNumber: string;
}

const PhoneConfirmOTPForm = ({
  buildSubmitHandler,
  countryCode,
  formFields: { passcode },
  isLoading,
  loginStart,
  loginSuccess,
  onClickBack,
  onResendOtpTokenSuccess,
  otpToken,
  phoneNumber,
}: PhoneConfirmOTPFormProps) => {
  const translate = useSimpleTranslate<OtpLoginPhoneTranslations>();
  const [formError, setFormError] = useState<string>();
  const [isResendingOtpToken, setIsResendingOtpToken] = useState(false);

  useEffect(() => {
    onShowOtpPhoneConfirmStep();
    trackOtp(OTP_TRACKING_EVENT.VIEWED_CONFIRM_PASSCODE);
  }, []);

  const handleSubmitPasscode = buildSubmitHandler(async (formData) => {
    loginStart();
    trackOtp(OTP_TRACKING_EVENT.CONFIRM_LOGIN_CLICK);

    const recaptchaTokenOrError = await getRecaptchaToken({ translate });

    const data =
      typeof recaptchaTokenOrError === "string"
        ? await loginWithOTP({
            passcode: formData.passcode,
            recaptchaToken: recaptchaTokenOrError,
            token: otpToken,
          })
        : recaptchaTokenOrError;

    if ("error" in data) {
      dispatchLoginFailure(data);
      return setFormError(data.errors[0].message ?? undefined);
    }

    return loginSuccess(data);
  });

  const handleOTPTokenResend = async () => {
    setIsResendingOtpToken(true);
    trackOtp(OTP_TRACKING_EVENT.RESEND_PASSCODE_CLICK);

    const recaptchaTokenOrError = await getRecaptchaToken({ translate });

    const data =
      typeof recaptchaTokenOrError === "string"
        ? await generateOTP({
            body: {
              countryCode,
              method: OTP_METHODS.SMS,
              phoneNumber,
            },
            recaptchaToken: recaptchaTokenOrError,
          })
        : recaptchaTokenOrError;

    setIsResendingOtpToken(false);

    if ("error" in data) {
      dispatchLoginFailure(data);
      return setFormError(data.errors[0].message ?? undefined);
    }

    /**
     * The current behavior sends the same OTP token (and same SMS code).
     * Should this behavior change, this will ensure the token is not out
     * of sync with the code we send.
     */
    return onResendOtpTokenSuccess(data.token);
  };

  const phoneNumberDisplay = `${translate(
    "auth.login.otp.phone.passcode.helper_text",
  )} ${formatPhoneNumber({ countryCode, phoneNumber })}`;

  return (
    <OTPForm onSubmit={handleSubmitPasscode}>
      <StyledText marginBottom={0} styleName="bodyMobile">
        {phoneNumberDisplay}
      </StyledText>
      <TextInput
        autoComplete="one-time-code"
        id="passcode"
        inputMode="numeric"
        label={translate("auth.login.otp.phone.passcode.form.label")}
        name="passcode"
        onBlur={passcode.onBlur}
        onChange={passcode.onChange}
        onInput={passcode.onBlur}
        placeholder={translate(
          "auth.login.otp.phone.passcode.form.placeholder",
        )}
        status={!!passcode.error || formError ? "error" : undefined}
        supportingText={formError}
        type={FIELD_TYPES.TEXT}
        value={passcode.value}
      />
      <OtpResendPasscodeButton
        // `handleOTPTokenResend` is async but still returns void
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        handleOTPTokenResend={handleOTPTokenResend}
        isResendingOtpToken={isResendingOtpToken}
        otpMethod={OTP_METHODS.SMS}
      />
      <OTPButtonContainer>
        <PrimaryButton
          isDisabled={isPasscodeEmpty(passcode.value) || isLoading}
          isLoading={isLoading}
          type="submit"
        >
          {translate("auth.login.otp.phone.form.continue.button_label")}
        </PrimaryButton>
        <GhostButton onClick={onClickBack}>
          {translate("auth.login.otp.phone.back.button_label")}
        </GhostButton>
      </OTPButtonContainer>
    </OTPForm>
  );
};

export default stateForm(PhoneConfirmOTPForm, {
  types: {
    passcode: FIELD_TYPES.NUMBER,
  },
  validate: buildFormValidate({
    passcode: otpPasscodeValidate(),
  }),
});
